import { useMatchRoute } from "@tanstack/react-router";
import * as React from "react";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { Collapse, ListItemButton, useMediaQuery } from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { NavItemType } from "./types";
import NavItem from "./NavItem";
import checkIfItemSelected from "./utils/checkIfItemSelected";

import MenuIcon from "./MenuIcon";

interface DrawerCollapseProps {
  icon: React.ReactNode;
  title: string;
  titleComponent?: React.ReactNode;
  items: NavItemType[];
  isOpen?: boolean;
  autoCloseDrawer: () => void;
}

export default function DrawerCollapse({
  icon,
  title,
  titleComponent,
  items,
  isOpen,
  autoCloseDrawer,
}: DrawerCollapseProps) {
  const matchRoute = useMatchRoute();
  // The section expands with a height animation, which moves every row below
  // the one being revealed for its whole duration. A visitor who has asked the
  // OS for reduced motion should not be shown that (WCAG 2.3.3), so the section
  // simply snaps open for them — same markup, same rows, no travel.
  const prefersReducedMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)"
  );
  const [open, setOpen] = React.useState(isOpen ?? false);
  const handleClick = () => {
    setOpen(!open);
  };

  return (
    <>
      <ListItemButton
        key={"key-list-item-" + title}
        onClick={handleClick}
        data-testid={`DrawerCollapseLink_${title}`}
        selected={items?.some((item) => checkIfItemSelected(matchRoute, item))}
        sx={{ paddingLeft: "8px", paddingRight: "8px" }}
      >
        {icon && (
          <ListItemIcon sx={{ minWidth: "36px" }}>
            <MenuIcon icon={icon} selected={false} />
          </ListItemIcon>
        )}
        <ListItemText primary={titleComponent ?? title} />
        {open ? (
          <ExpandLess fontSize="small" />
        ) : (
          <ExpandMore fontSize="small" />
        )}
      </ListItemButton>
      <Collapse
        key={"key-list-item-collapse-" + title}
        in={open}
        timeout={prefersReducedMotion ? 0 : "auto"}
        unmountOnExit
      >
        {items.map((item, index) => (
          <NavItem
            key={`NavItemType_${index}`}
            {...item}
            autoCloseDrawer={autoCloseDrawer}
            icon={undefined}
            variant="list-item"
          />
        ))}
      </Collapse>
    </>
  );
}
